import DOMPurify from 'dompurify';

export const ALLOWED_TAGS = [
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'span',
  'strong',
  'u',
  'ul',
];

export const ALLOWED_ATTR = [
  'class',
  'src',
  'alt',
  'width',
  'height',
  'href',
  'rel',
  'target',
  'data-mention-type',
  'data-mention-id',
  'data-mention-name',
  'data-mention-slug',
  'data-mention-href',
  'data-emoji-key',
  'data-emoji-type',
  'data-emoji-fallback',
  'contenteditable',
  'tabindex',
  'title',
];

export const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<]+)/gi;

export const sanitizeComposerHtml = (html) =>
  DOMPurify.sanitize(html || '', {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: true,
    RETURN_TRUSTED_TYPE: false,
  });

const isBlankHtmlBlock = (value) => {
  const stripped = value
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim();
  return stripped.length === 0;
};

export const normalizeComposerOutput = (rawHtml, { sanitize = true } = {}) => {
  if (!rawHtml) return '';
  let normalized = rawHtml;

  if (!/<[a-z][\s>]/i.test(normalized) && /\r?\n/.test(normalized)) {
    const blocks = normalized.split(/\r?\n{2,}/);
    normalized = blocks
      .map((block) => block.replace(/\r?\n/g, '<br>'))
      .map((block) => `<p>${block.trim().length ? block : '<br>'}</p>`)
      .join('');
  }

  normalized = sanitize ? sanitizeComposerHtml(normalized) : normalized;

  const divs = normalized.match(/<div[^>]*>[\s\S]*?<\/div>/gi);
  if (divs) {
    const nonDivRemainder = normalized.replace(/<div[^>]*>[\s\S]*?<\/div>/gi, '').trim();

    // Preserve mixed markup where text can live outside <div> blocks.
    if (nonDivRemainder.length > 0) {
      normalized = normalized.replace(/<div[^>]*>/gi, '<p>').replace(/<\/div>/gi, '</p>');
    } else {
      const paragraphs = [];
      let current = [];
      divs.forEach((div) => {
        const inner = div.replace(/^<div[^>]*>/i, '').replace(/<\/div>$/i, '');
        if (isBlankHtmlBlock(inner)) {
          if (current.length) {
            paragraphs.push(current);
            current = [];
          } else {
            paragraphs.push(['<br>']);
          }
        } else {
          current.push(inner);
        }
      });
      if (current.length) paragraphs.push(current);
      return paragraphs.map((lines) => `<p>${lines.join('<br>')}</p>`).join('');
    }
  }

  normalized = normalized.replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '</p><p>');
  if (!/<p[\s>]/i.test(normalized)) {
    normalized = `<p>${normalized}</p>`;
  }
  normalized = normalized.replace(/<p>\s*<\/p>/gi, '<p><br></p>');
  return normalized;
};

export const stripHtmlToPlain = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined') {
    const withEmojiFallback = html.replace(
      /<img[^>]*data-emoji-type=['\"]custom['\"][^>]*>/gi,
      (match) => {
        const fallbackMatch = match.match(/data-emoji-fallback=['\"]([^'\"]*)['\"]/i);
        const altMatch = match.match(/alt=['\"]([^'\"]*)['\"]/i);
        const fallback = fallbackMatch?.[1] || altMatch?.[1] || '';
        return fallback || ' ';
      },
    );
    return withEmojiFallback.replace(/<[^>]+>/g, ' ');
  }
  const container = window.document.createElement('div');
  container.innerHTML = html;
  const emojiNodes = container.querySelectorAll('img[data-emoji-type="custom"]');
  emojiNodes.forEach((node) => {
    const fallback = node.getAttribute('data-emoji-fallback') || node.getAttribute('alt') || '';
    const textNode = window.document.createTextNode(fallback || '');
    node.replaceWith(textNode);
  });
  return container.textContent || container.innerText || '';
};

export const normalizeUrl = (raw) => {
  if (!raw) return null;
  try {
    let url = String(raw).trim();
    if (!url) return null;
    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }
    const parsed = new URL(url);
    const hostname = parsed.hostname;
    if (!hostname || !hostname.includes('.')) return null;
    const segments = hostname.split('.');
    const tld = segments[segments.length - 1];
    if (!tld || tld.length < 2) return null;

    parsed.hash = '';
    return parsed.toString();
  } catch (err) {
    return null;
  }
};

export const siteNameFromUrl = (rawUrl) => {
  if (!rawUrl) return '';
  try {
    const { hostname } = new URL(rawUrl);
    return hostname.replace(/^www\./i, '') || hostname;
  } catch (err) {
    return '';
  }
};

export const extractUrls = (text) => {
  if (!text) return [];
  const regex = new RegExp(URL_REGEX);
  const found = new Set();
  let match;
  while ((match = regex.exec(text))) {
    const candidate = match[1]?.replace(/[).,!]+$/, '');
    const normalized = normalizeUrl(candidate);
    if (normalized) found.add(normalized);
  }
  return Array.from(found);
};

export const mapInitialPreviews = (previews) => {
  const map = {};
  if (!Array.isArray(previews)) return map;
  previews.forEach((preview) => {
    const url = normalizeUrl(preview?.url);
    if (!url) return;
    map[url] = {
      status: 'ready',
      data: {
        url,
        siteName: preview?.siteName || siteNameFromUrl(url),
        title: preview?.title || '',
        description: preview?.description || '',
        imageUrl: preview?.imageUrl || '',
      },
    };
  });
  return map;
};

export const buildMentionsByType = (mentions = []) => {
  return mentions.reduce((acc, mention) => {
    const type = mention?.type || 'user';
    if (!acc[type]) acc[type] = [];
    acc[type].push(mention);
    return acc;
  }, {});
};
